import React, { useState } from "react"
import * as uuid from "uuid"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as icons from "icons"
import * as md5x from "md5x"
import * as api from "brandguard/api"
import * as training from "brandguard/display/training"

interface props extends layouts.containers.FlexProps {
	settings: api.ImageKernelLookandFeelEnabled
	onChange(upd: api.ImageKernelLookandFeelEnabled): void
}

export default function CustomRules(props: React.PropsWithChildren<props>): JSX.Element {
	const labelwidth = "12ch"
	const { settings, onChange, ...rest } = props
	const [description, setDescription] = useState("")
	const [rule, setRule] = useState("")
	const [reset, setReset] = useState(uuid.v4())
	const [editingId, setEditingId] = useState(null as string | null)
	const [editedDescription, setEditedDescription] = useState("")
	const [editedRule, setEditedRule] = useState("")

	const handleSaveEdit = (originalId: string) => {
		const newId = md5x.string(editedRule)
		onChange({
			...settings,
			rules: settings.rules.map((o) =>
				o.id === originalId ? { id: newId, description: editedDescription, rule: editedRule } : o,
			),
		})
		setEditingId(null)
		setEditedDescription("")
		setEditedRule("")
	}

	const rules = (settings.rules || []).map((r) => (
		<layouts.Flex key={r.id} py="5px" flexDirection="column">
			{editingId === r.id ? (
				<>
					<layouts.forms.Group>
						<layouts.forms.Label width={labelwidth}>Description</layouts.forms.Label>
						<inputs.Text
							autoFocus
							defaultValue={editedDescription}
							onChange={(evt) => setEditedDescription(evt.target.value)}
							color={layouts.theme.colors.grey.dark20}
							className={training.layouts.styledTextInput}
						/>
					</layouts.forms.Group>
					<layouts.forms.Group>
						<layouts.forms.Label width={labelwidth}>Rule</layouts.forms.Label>
						<inputs.Text
							defaultValue={editedRule}
							onChange={(evt) => setEditedRule(evt.target.value)}
							color={layouts.theme.colors.grey.dark20}
							className={training.layouts.styledTextInput}
						/>
					</layouts.forms.Group>
					<layouts.Flex mt="10px">
						<layouts.buttons.outline
							onClick={() => {
								setEditingId(null)
								setEditedDescription("")
								setEditedRule("")
							}}
							width="10ch"
							borderRadius="5px"
						>
							Cancel
						</layouts.buttons.outline>
						<layouts.buttons.primary onClick={() => handleSaveEdit(r.id)} ml="10px" width="10ch" borderRadius="5px">
							Save
						</layouts.buttons.primary>
					</layouts.Flex>
				</>
			) : (
				<>
					<layouts.Flex>
						<layouts.Span>{r.description}</layouts.Span>
						<layouts.Span
							ml="5px"
							onClick={(evt) => {
								evt.stopPropagation()
								setEditingId(r.id)
								setEditedDescription(r.description)
								setEditedRule(r.rule)
							}}
							style={{ cursor: "pointer" }}
						>
							<icons.Edit width="14px" height="14px" fill={layouts.theme.colors.grey.dark20} />
						</layouts.Span>
						<layouts.Span
							ml="auto"
							onClick={(evt) => {
								evt.stopPropagation()
								onChange({ ...settings, rules: settings.rules.filter((o) => o.id !== r.id) })
							}}
							style={{ cursor: "pointer" }}
						>
							<icons.Trash width="14px" height="14px" fill={layouts.theme.colors.grey.dark20} />
						</layouts.Span>
					</layouts.Flex>
					<layouts.Span>{r.rule}</layouts.Span>
				</>
			)}
		</layouts.Flex>
	))

	return (
		<layouts.Flex flexDirection="column" flex="1" {...rest}>
			<layouts.forms.Container key={reset} p="10px">
				<layouts.forms.Group>
					<layouts.forms.Label width={labelwidth}>Description</layouts.forms.Label>
					<inputs.Text
						autoFocus
						placeholder="short description of the rule"
						onChange={(evt) => setDescription(evt.target.value)}
						className={training.layouts.styledTextInput}
					/>
				</layouts.forms.Group>
				<layouts.forms.Group>
					<layouts.forms.Label width={labelwidth}>Rule</layouts.forms.Label>
					<inputs.Text
						placeholder="explain the rule to enforce"
						onChange={(evt) => setRule(evt.target.value)}
						className={training.layouts.styledTextInput}
					/>
				</layouts.forms.Group>
				<layouts.Flex flex="1" flexDirection="row" justifyContent="space-between" mt="10px">
					<layouts.buttons.outline onClick={() => setReset(uuid.v4())} width="10ch" borderRadius="5px">
						Cancel
					</layouts.buttons.outline>
					<layouts.buttons.primary
						onClick={() => {
							onChange({
								...settings,
								rules: (settings.rules || []).concat({ id: md5x.string(rule), description: description, rule: rule }),
							})
							setReset(uuid.v4())
						}}
						borderRadius="5px"
						width="10ch"
					>
						Add
					</layouts.buttons.primary>
				</layouts.Flex>
			</layouts.forms.Container>
			<layouts.Flex flexDirection="column">{rules}</layouts.Flex>
		</layouts.Flex>
	)
}
